import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, ChevronDown } from "lucide-react";
import Logo from "./Logo";
import UserMenu from "./UserMenu";
import { CITIES, SITE, phoneDisplay } from "@/lib/site";
import { useAuth } from "@/lib/auth";

const links = [
  { to: "/portfolio", label: "Portfolio", testid: "nav-link-portfolio" },
  { to: "/process", label: "Process", testid: "nav-link-process" },
  { to: "/about", label: "About", testid: "nav-link-about" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [citiesOpen, setCitiesOpen] = useState(false);
  const { user, loading, signIn, logout } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-nav border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="container-x flex h-[76px] items-center justify-between">
          <Link to="/" data-testid="nav-logo" aria-label="Shriya Redkar Interiors — home">
            <Logo />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `font-display text-[12px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-zinc-300 hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <div className="relative" onMouseEnter={() => setCitiesOpen(true)} onMouseLeave={() => setCitiesOpen(false)}>
              <button
                data-testid="nav-cities-trigger"
                className="flex items-center gap-1.5 font-display text-[12px] font-medium uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:text-gold"
              >
                Cities
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${citiesOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {citiesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.25 }}
                    className="absolute right-0 top-full w-60 border border-white/10 bg-obsidian-soft/95 p-2 backdrop-blur-xl"
                  >
                    {CITIES.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/interiors/${c.slug}`}
                        data-testid={`nav-link-city-${c.slug}`}
                        className="flex items-center justify-between px-4 py-2.5 text-sm text-zinc-300 transition-colors hover:bg-white/5 hover:text-gold"
                      >
                        {c.name}
                        <ArrowUpRight className="h-3.5 w-3.5 opacity-0 transition-opacity hover:opacity-100" />
                      </Link>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          <div className="hidden items-center gap-6 lg:flex">
            <a
              href={`tel:${SITE.phoneHref}`}
              data-testid="nav-phone-link"
              className="font-mono text-[12px] tracking-wider text-zinc-400 transition-colors hover:text-gold"
            >
              {phoneDisplay}
            </a>
            <Link to="/enquire" data-testid="nav-cta-enquire" className="btn-gold !px-6 !py-3">
              Enquire
            </Link>
            {!loading &&
              (user ? (
                <UserMenu user={user} />
              ) : (
                <button
                  onClick={() => signIn("/account")}
                  data-testid="nav-signin-button"
                  className="font-display text-[12px] font-medium uppercase tracking-[0.18em] text-zinc-300 transition-colors hover:text-gold"
                >
                  Sign in
                </button>
              ))}
          </div>

          <button
            className="flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-100 lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col bg-obsidian/98 pt-[76px] backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="container-x flex flex-1 flex-col justify-between overflow-y-auto py-10">
              <nav className="flex flex-col gap-2">
                {links.map((l, i) => (
                  <motion.div
                    key={l.to}
                    initial={{ opacity: 0, x: -24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-${l.testid}`}
                      className="block border-b border-white/10 py-4 font-serif text-4xl text-zinc-100"
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + links.length * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="eyebrow pt-8">Cities we build in</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {CITIES.map((c) => (
                      <Link
                        key={c.slug}
                        to={`/interiors/${c.slug}`}
                        data-testid={`mobile-link-city-${c.slug}`}
                        className="border border-white/15 px-4 py-2 text-sm text-zinc-300"
                      >
                        {c.name}
                      </Link>
                    ))}
                  </div>
                </motion.div>
              </nav>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="mt-10 flex flex-col gap-4"
              >
                <Link to="/enquire" data-testid="mobile-cta-enquire" className="btn-gold w-full">
                  Start your enquiry
                </Link>
                {user ? (
                  <div className="flex items-center justify-between gap-4 border border-white/15 px-4 py-3" data-testid="mobile-user-row">
                    <Link to="/account" data-testid="mobile-link-account" className="min-w-0 text-sm text-zinc-200">
                      <span className="block truncate">{user.name}</span>
                      <span className="block text-xs text-zinc-500">My account{user.role === "admin" ? " · Studio dashboard" : ""}</span>
                    </Link>
                    <button onClick={() => logout()} data-testid="mobile-logout-button" className="shrink-0 text-xs uppercase tracking-[0.16em] text-zinc-400 hover:text-red-400">
                      Sign out
                    </button>
                  </div>
                ) : (
                  <button onClick={() => signIn("/account")} data-testid="mobile-signin-button" className="btn-ghost w-full">
                    Sign in with Google
                  </button>
                )}
                <a href={`tel:${SITE.phoneHref}`} className="text-center font-mono text-sm text-zinc-400">
                  {phoneDisplay}
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
